import { HttpErrorResponse } from '@angular/common/http';
import { formatCurrency, getCurrencySymbol } from '@angular/common';
import { addressLines } from '../transactions-layout/models/transaction';
import { Component, Inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantChangeService, NotificationService, OdinFeatures, StaticHelpers, } from '@odin/odin-core';
import {
  IFormattedTransaction,
  ITransaction,
  TransactionRefund,
  TransactionStatus, BasketItem, BasketDiscount, BasketTax, BasketDelivery, BasketInvoice,
  transactionIcon,
  transactionIconTooltip,
} from '../transactions-layout/models';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TransactionFormatterService, TransactionsService, } from '../transactions-layout/services';
import { AccessGuardService, RoleGuardService } from '@odin/odin-authentication';
import {
  RechargeModalData,
  RechargeTransactionModalComponent
} from '../transactions-layout/components/recharge-transaction-modal/recharge-transaction-modal.component';
import {
  RefundModalData,
  RefundTransactionModalComponent
} from '../transactions-layout/components/refund-transaction-modal/refund-transaction-modal.component';
import {
  ReverseModalData,
  ReverseTransactionModalComponent
} from '../transactions-layout/components/reverse-transaction-modal/reverse-transaction-modal.component';
import { TransactionRecharge } from '../transactions-layout/models/transaction-recharge';
import { MatDialog } from '@angular/material/dialog';
import { ReceiptDetail } from '../print-receipt/print-receipt.component';
import { printPrintableContent } from '../print-receipt/print-helper';
import { InvoiceDetail, InvoiceOptions } from '../print-invoice/print-invoice.component';
import {
  InvoiceModalData,
  InvoiceOptionsModalComponent
} from '../transaction-detail-page/components/invoice-options-modal/invoice-options-modal.component';
import { ReceiptSettingsService } from "@odin/odin-settings";
import {
  CompleteModalData,
  CompleteTransactionModalComponent
} from "../transactions-layout/components/complete-transaction-modal/complete-transaction-modal.component";
import { WebhooksMessageAttempt } from '../transactions-layout/models/WebhooksMessageAttempt';
import { CallbackTransactionModalComponent } from '../transactions-layout/components/callback-transaction-modal/callback-transaction-modal.component';

export class TransactionTimelineEntry {
  constructor(
    public status: boolean,
    public title: string,
    public time: Date,

    public transaction: IFormattedTransaction,
    public isThis: boolean,
  ) { }
}

@Component({
  standalone: false,
  selector: 'odin-transaction-detail-page-legacy',
  templateUrl: './transaction-detail-page-legacy.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './transaction-detail-page-legacy.component.css',
})
export class LegacyTransactionDetailPageComponent implements OnInit {
  public loading: boolean = true;
  public callbacksLoading: boolean = true;
  private transactionTraceId: string;
  public transactionType: string = 'Transaction';
  public transaction: WritableSignal<IFormattedTransaction> =
    signal<IFormattedTransaction>({} as IFormattedTransaction);
  public relatedTransactions: WritableSignal<IFormattedTransaction[]> = signal<
    IFormattedTransaction[]
  >([]);
  public hasRelated: WritableSignal<boolean> = signal<boolean>(false);
  public relatedLoading: WritableSignal<boolean> = signal<boolean>(false);
  public timelineEvents: TransactionTimelineEntry[] = [];
  public shelper = StaticHelpers;
  svixFeatureEnabled: boolean;
  callbackAttempts: WebhooksMessageAttempt[] = [];

  constructor(
    private dialog: MatDialog,
    private router: Router,
    private accessService: AccessGuardService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private transactionService: TransactionsService,
    private transactionFormatter: TransactionFormatterService,
    private merchantChangeService: MerchantChangeService,
    private receiptService: ReceiptSettingsService,
    private roleGuardService: RoleGuardService,
    @Inject('features') public featureHelper: OdinFeatures,
  ) {
  }

  ngOnInit(): void {
    this.merchantChangeService.onChange('transaction detail page', window.location.href, (buid: string) => {
      // transactions not available on different buid, so back out to transactions
      this.router.navigate(['transactions']);
    });
    this.transactionTraceId = this.activatedRoute.snapshot.params['traceId'];
    this.refreshTransaction();
    if (this.canAccessWebhooks()) {
      this.getCallbackDetails();
    }
  }

  public refreshTransaction(): void {
    this.loading = true;
    this.relatedTransactions.set([]);
    this.transaction.set({} as IFormattedTransaction);
    this.fetchTransactionDetail();
  }

  private fetchTransactionDetail(): void {
    this.loading = true;
    // Get monekId from query params if present
    const monekId = this.activatedRoute.snapshot.queryParamMap.get('monekid') || undefined;
    this.transactionService
      .getTransaction(this.transactionTraceId, monekId)
      .subscribe({
        next: (t: ITransaction) => {
          this.transaction.set(this.transactionFormatter.formatTransaction(t));
          this.transactionType = this.GetTransactionType(t);
          this.loading = false;
          this.getRelated();
        },
        error: (err) => {
          this.loading = false;
          this.router.navigate(['transactions']);
          this.notificationService.ErrorSmallDialog(
            'Error fetching transaction details for ' + this.transactionTraceId, err
          );
        }
      });
  }

  public GetTransactionType(transaction: ITransaction): string {
    return transaction.transactionType === 'refund'
      ? 'Refund'
      : transaction.transactionType === 'reversal' ?
        'Reversal'
        : transaction.isReversed
          ? 'Reversed Transaction'
          : 'Transaction';
  }

  public isSuccess(
    transaction: ITransaction,
    reverseIsSuccess: boolean = false,
  ): boolean {
    if (transaction.status === TransactionStatus.Succeeded) return true;
    if (transaction.status === TransactionStatus.PreAuthSuccess) return true;
    if (reverseIsSuccess && transaction.status === TransactionStatus.Reversed)
      return true;
    return false;
  }

  public isCompletedPreAuth(): boolean {
    if (this.relatedLoading()) return false;
    if (this.relatedTransactions().length > 0) {
      const _related = this.relatedTransactions();
      return _related.findIndex(r => r.status === TransactionStatus.PreAuthSuccess) > -1;
    }
    return false;
  }

  public transactionIcon(transaction: ITransaction): IconProp {
    return transactionIcon(transaction);
  }

  public transactionIconTooltip(transaction: IFormattedTransaction): string {
    return transactionIconTooltip(transaction);
  }

  public showRefundAmount(): boolean {
    if (this.transaction().amountRefunded !== undefined) return false;
    const amountRefunded = this.transaction().amountRefunded;
    if (amountRefunded === undefined) return false;
    if (amountRefunded <= 0) return false;
    return true;
  }

  public showPaymentDescription(): boolean {
    if (this.transaction().paymentDetail === undefined) return false;
    if (this.transaction().paymentDetail === null) return false;
    if (this.transaction().paymentDetail.length === 0) return false;
    return true;
  }

  private getRelated(): void {
    if (this.transaction().channel.toLowerCase() === 'cardholder_present' && this.transaction().transactionType === 'refund') {
      this.hasRelated.set(false);
      return;
    }

    this.relatedTransactions.set([]);
    this.hasRelated.set(true);
    this.relatedLoading.set(true);
    if (this.transaction().transactTransactionType && this.transaction().transactTransactionType.toUpperCase().startsWith('PAYMENT_ONLY_')) {
      this.fetchRelatedTransactions('parent'); // support for pre-auth completion finding its original payment
    }

    this.fetchRelatedTransactions();
  }

  private fetchRelatedTransactions(forcedRelation?: 'parent' | 'children'): void {
    this.transactionService
      .GetRelatedTransactions(this.transaction(), forcedRelation)
      .subscribe(
        (res: ITransaction[]) => {
          this.setRelated(res);
          this.createTimeline();
        },
        (err: unknown) => {
          this.notificationService.ErrorSmallDialog(
            'Failed to fetch related transactions.', err
          );
          this.createTimeline();
          this.relatedLoading.set(false);
        },
      );
  }


  private setRelated(fetchedRelatedTransactions: ITransaction[]): void {
    this.relatedTransactions.set(
      [
        ...this.relatedTransactions(),
        ...fetchedRelatedTransactions.map((t: ITransaction) => {
          return this.transactionFormatter.formatTransaction(t);
        })],
    );

    this.relatedTransactions().forEach((t: IFormattedTransaction) => {
      if (t.transactionType !== 'refund') return;
      if (t.status === 'declined') return;
      if (this.transaction === undefined) return;
      this.transaction.update((value: IFormattedTransaction) => {
        if (value.amountRefunded == undefined) value.amountRefunded = 0;
        if (value.remainingValue == undefined) value.remainingValue = 0;

        value.remainingValue = value.remainingValue - t.amount;
        value.amountRefunded = value.amountRefunded + t.amount;
        return value;
      });
    });

    this.relatedTransactions.set(
      this.relatedTransactions().sort(
        (a, b) =>
          new Date(a.createdOn).getTime() -
          new Date(b.createdOn).getTime(),
      ),
    );

    this.relatedLoading.set(false);
  }

  public canPrint(): boolean {
    if (this.transaction().status === TransactionStatus.Cancelled)
      return false;
    return true;
  }

  // ** Webhooks **
  public canAccessWebhooks(): boolean {
    const hasBuidFeature = this.accessService.buidHasFeature('svix');
    const hasPermission = this.accessService.hasNewGrant('int:Manage') || this.roleGuardService.hasPermission('settings:Webhooks');

    this.svixFeatureEnabled = hasBuidFeature && hasPermission;
    return this.svixFeatureEnabled;
  }

  public startResendCallback(): void {
    this.loading = true;
    this.transactionService
      .resendTransactionCallback(this.transactionTraceId)
      .subscribe(() => {
        this.notificationService.SmallDialog(
          'Transaction callback sent successfully',
        );
        this.loading = false;
      }, (err: unknown) => {
        this.notificationService.ErrorSmallDialog(
          'Failed to send transaction callback', err,
        );
        this.loading = false;
      });
  }
  public getCallbackDetails(): void {
    this.callbacksLoading = true;
    this.transactionService
      .getTransactionCallback(this.transactionTraceId)
      .subscribe({
        next: (response) => {
          if (Array.isArray(response)) {
            this.callbackAttempts = response;
          } else if (response) {
            this.callbackAttempts = [response];
          } else {
            this.callbackAttempts = [];
          }
          this.callbacksLoading = false;
        },
        error: (err: unknown) => {
          if (!(err instanceof HttpErrorResponse && err.status === 404)) {
            this.notificationService.ErrorSmallDialog('Failed to load callback details', err);
          }
          this.callbackAttempts = [];
          this.callbacksLoading = false;
        },
      });
  }

  public callbackDialogBox(attempt: WebhooksMessageAttempt): void {
    this.dialog.open(CallbackTransactionModalComponent, {
      width: '600px',
      data: attempt
    });
  }

  public isTransactDay(): boolean {
    // check happened TODAY before 23:59pm
    const transactionTime = new Date(this.transaction().createdOn);
    const thisMorning = new Date(Date.now());
    thisMorning.setHours(0);
    thisMorning.setMinutes(0);
    thisMorning.setSeconds(0);
    thisMorning.setMilliseconds(0);
    const tomorrow = new Date(thisMorning);
    tomorrow.setHours(23);
    tomorrow.setMinutes(59);
    tomorrow.setSeconds(59);
    if (transactionTime > thisMorning && transactionTime < tomorrow)
      return false;
    return true;
  }

  public isTransactWeek(): boolean {
    const transactionTime = new Date(this.transaction().createdOn);
    const now = new Date();

    const diffInMs = now.getTime() - transactionTime.getTime();
    const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

    return diffInDays <= 7;
  }

  public RelatedHasCompletedSale(): boolean {
    let hasCompleteRelatedTransaction = false;
    this.relatedTransactions().forEach((transaction: IFormattedTransaction) => {
      // ensure transaction is after pre-auth (technically impossible)
      if (new Date(transaction.createdOn).getTime() > new Date(this.transaction().createdOn).getTime()) {
        if (transaction.status === TransactionStatus.Succeeded) hasCompleteRelatedTransaction = true;
      }
    });
    return hasCompleteRelatedTransaction;
  }

  public canReverse(): boolean {
    if (!this.accessService.hasNewGrant('txn:Reverse') && !this.roleGuardService.hasPermission('txn:Refund'))
      return this.LogAndReturn('No txn:Reverse Grant', false);
    if (this.transaction().isReversed)
      return this.LogAndReturn('Is Reversed', false);
    if (this.transaction().isReversal)
      return this.LogAndReturn('Is Reversal', false);
    if (this.transaction().status === TransactionStatus.Cancelled)
      return this.LogAndReturn('Is Cancelled', false);
    if (this.isTransactDay())
      return this.LogAndReturn('Is Transact Day', false);
    if (this.transaction().isPreAuth)
      return this.LogAndReturn('Is PreAuth', false);
    if (this.relatedLoading()) return false;
    return true;
  }

  public canRefund(): boolean {
    if (!this.accessService.hasNewGrant('txn:Refund') && !this.roleGuardService.hasPermission('txn:Refund'))
      return this.LogAndReturn('No txn:Refund Grant', false);
    if (this.transaction().transactionType === 'refund')
      return this.LogAndReturn('Is Refund', false);
    if (this.transaction().isReversed)
      return this.LogAndReturn('Is Reversed', false);
    if (this.transaction().isReversal)
      return this.LogAndReturn('Is Reversal', false);
    if (this.transaction().status === TransactionStatus.Cancelled)
      return this.LogAndReturn('Is Cancelled', false);
    if (this.transaction().isPreAuth)
      return this.LogAndReturn('Is PreAuth', false);
    if (this.relatedLoading()) return false;
    return true;
  }

  public canReCharge(): boolean {
    if (!this.accessService.hasNewGrant('txn:Recharge') && !this.roleGuardService.hasPermission('txn:Create'))
      return this.LogAndReturn('No txn:Recharge Grant', false);
    if (this.transaction().transactionType === 'refund')
      return this.LogAndReturn('Is Refund', false);
    if (this.transaction().isReversed)
      return this.LogAndReturn('Is Reversed', false);
    if (this.transaction().isReversal)
      return this.LogAndReturn('Is Reversal', false);
    if (this.transaction().status === TransactionStatus.Cancelled)
      return this.LogAndReturn('Is Cancelled', false);
    if (this.relatedLoading()) return false;
    return true;
  }

  public canComplete(): boolean {
    if (!this.featureHelper['txn-complete'])
      return this.LogAndReturn('txn-complete feature disabled', false);
    if (!this.accessService.hasNewGrant('txn:Create') && !this.roleGuardService.hasPermission('txn:Create'))
      return this.LogAndReturn('No txn:Create Grant', false);
    if (!this.transaction().isPreAuth)
      return this.LogAndReturn('Isnt PreAuth', false);
    if (this.transaction().status === TransactionStatus.PreAuthFailed)
      return this.LogAndReturn('Invalid Preauth (failed)', false);
    if (!this.isTransactWeek())
      return this.LogAndReturn('Isnt within a week', false);
    if (this.relatedLoading()) return false;
    if (this.RelatedHasCompletedSale())
      return this.LogAndReturn('Preauth is already complete', false);
    return true;
  }

  private createTimeline(): void {
    // add open transaction
    this.timelineEvents = [];
    const entry = new TransactionTimelineEntry(
      this.isSuccess(this.transaction()),
      this.isCompletedPreAuth() ? 'Completed Pre-Auth' : this.transaction().isPreAuth ?
        (!this.isSuccess(this.transaction()) ? 'Pre-Auth Declined' : 'Pre-Authorised') :
        (!this.isSuccess(this.transaction()) ? 'Declined ' : 'Completed ') + this.transaction().formattedType,
      this.transaction().createdOn,
      this.transaction(),
      true
    );
    this.timelineEvents.push(entry);

    // add in related
    this.timelineEvents.push(
      ...this.relatedTransactions().map((related: IFormattedTransaction) => {

        if (this.isCompletedPreAuth() || this.transaction().isPreAuth && (related.transactTransactionType && related.transactTransactionType.toUpperCase().startsWith('PAYMENT_ONLY_'))) {
          related.formattedType = 'Completed Pre-Auth';
        }

        return new TransactionTimelineEntry(
          this.isSuccess(related, true),
          related.formattedType,
          related.createdOn,
          related,
          false
        );
      }),
    );

    this.timelineEvents.push();

    this.timelineEvents = this.timelineEvents.sort(
      (a, b) => new Date(a.time).getTime() - new Date(b.time).getTime(),
    );

  }

  public OpenRelation(_transaction: IFormattedTransaction): void {
    const monekId = _transaction.merchant?.monekId;

    this.router.navigate(
      [`transactions/detail/${_transaction.crossReference}`],
      monekId ? { queryParams: { monekid: monekId } } : undefined
    );
  }

  public BackButtonHandle(): void {
    this.router.navigate(['transactions']);
  }


  //

  public getStackTrace(): string {
    try {
      throw new Error('');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      return error.stack || '';
    }
  }

  public LogAndReturn(
    msg: string,
    toReturn: boolean,
    logLevel: number = 0,
  ): boolean {
    if (window._audit_logs == undefined) window._audit_logs = [];
    window._audit_logs.push({
      level: logLevel,
      log: msg,
      stack: this.getStackTrace(),
    });
    return toReturn;
  }

  public createReceipt(): ReceiptDetail {
    const tx = this.transaction();
    return new ReceiptDetail(
      '',
      tx.crossReference,
      tx.cardHolderDetails !== undefined ? tx.cardHolderDetails.name || tx.maskedCard : '',
      tx.paymentDetail,
      tx.createdOn,
      '',
      tx.amount,
      tx.currencyCountry,
      this.GetTransactionType(tx),
      tx.maskedCard,
      tx.paymentReference,
      tx.operatorName,
      tx.amountRefunded,
      tx.remainingValue,
      tx.status ? tx.status.toString() : '',
      tx.basket,
      tx.cardHolderDetails?.postcode,
      tx.cardHolderDetails?.emailAddress,
      tx.deliveryAddress,
      tx.billingAddress
    );
  }

  public CustomJsonLines(base64JsonString: string): { key: string, value: string }[] {
    const json = StaticHelpers.decodeJsonB64(base64JsonString);

    return Object.keys(json).map((key) => {
      let value = json[key];

      if (value === null || value === undefined) value = '-'
      if (StaticHelpers.isArray(value)) value = JSON.stringify(value);
      if (StaticHelpers.isObject(value)) value = JSON.stringify(value);

      return { 'key': key, 'value': value };
    });
  }

  public hasBasketData(): boolean {
    const basket = this.transaction().basket;
    if (!basket) return false;
    return this.LineItems().length > 0 ||
           this.DiscountLineItems().length > 0 ||
           this.TaxLineItems().length > 0 ||
           !!basket.delivery ||
           !!basket.invoice;
  }

  public LineItems(): BasketItem[] {
    const basket = this.transaction().basket;
    if (basket === undefined) return [];
    return basket.basketItems?.filter(item => item !== null && item !== undefined) || [];
  }

  public DiscountLineItems(): BasketDiscount[] {
    const basket = this.transaction().basket;
    if (basket === undefined) return [];
    return basket.basketDiscounts?.filter(discount => discount !== null && discount !== undefined) || [];
  }

  public TaxLineItems(): BasketTax[] {
    const basket = this.transaction().basket;
    if (basket === undefined) return [];
    return basket.basketTaxes?.filter(tax => tax !== null && tax !== undefined) || [];
  }

  public BasketDelivery(): BasketDelivery | null {
    const basket = this.transaction().basket;
    if (basket === undefined) return null;
    if (basket.delivery === null || basket.delivery === undefined) return null;
    return basket.delivery;
  }

  public BasketInvoice(): BasketInvoice | null {
    const basket = this.transaction().basket;
    if (basket === undefined) return null;
    if (basket.invoice === null || basket.invoice === undefined) return null;
    return basket.invoice;
  }

  /** Thor's line total already accounts for any line discount; fall back to qty x unit price */
  public lineTotal(item: BasketItem): number {
    return item.total ?? (item.unitPrice ?? 0) * (item.quantity || 1);
  }

  public basketSubtotal(): number {
    return this.LineItems().reduce((sum, item) => sum + this.lineTotal(item), 0);
  }

  /** e.g. "VAT 20%", "20%", "VAT" or "" (a zero rate is omitted) */
  public taxLabel(code?: string, rate?: number): string {
    const parts: string[] = [];
    if (code) parts.push(code);
    if (rate) parts.push(`${rate}%`);
    return parts.join(' ');
  }

  /** e.g. "(10%, includes £0.60 VAT 20%)" */
  public discountLabel(discount: BasketDiscount): string {
    const parts: string[] = [];
    if (discount.rate) parts.push(`${discount.rate}%`);
    if (discount.taxAmount) {
      const currency = this.transaction().currencyCountry;
      const tax = formatCurrency(discount.taxAmount, 'en-GB', getCurrencySymbol(currency, 'narrow'), currency);
      parts.push(`includes ${tax} ${this.taxLabel(discount.taxCode, discount.taxRate)}`.trim());
    }
    return parts.length ? `(${parts.join(', ')})` : '';
  }

  /** number of basket lines (not the sum of quantities) */
  public basketItemCount(): number {
    return this.LineItems().length;
  }

  /** basket gross as Thor calculates it for a tax-inclusive or exempt basket; exclusive adds basket-level taxes */
  public basketTotal(): number {
    const delivery = this.BasketDelivery();
    const discounts = this.DiscountLineItems().reduce((sum, d) => sum + (d.amount ?? 0), 0);
    const taxes = this.TaxLineItems().reduce((sum, t) => sum + (t.amount ?? 0), 0);
    const exclusive = this.taxTypeLabel() === 'prices exclude tax';
    const itemTax = exclusive ? this.LineItems().reduce((sum, i) => sum + (i.taxAmount ?? 0), 0) : 0;
    const deliveryTax = exclusive ? (delivery?.taxAmount ?? 0) : 0;
    const discountTax = exclusive ? this.DiscountLineItems().reduce((sum, d) => sum + (d.taxAmount ?? 0), 0) : 0;
    const total = this.basketSubtotal() + itemTax + (delivery?.amount ?? 0) + deliveryTax - discounts - discountTax + (exclusive ? taxes : 0);
    return Math.round(total * 100) / 100;
  }

  /** signed difference between the amount charged and the basket total, or 0 when they agree */
  public basketDifference(): number {
    const diff = Math.round((this.transaction().amount - this.basketTotal()) * 100) / 100;
    return Math.abs(diff) < 0.005 ? 0 : diff;
  }

  /** tax rolled up by code and rate across items, discounts (negative), delivery and basket-level taxes */
  public taxSummary(): { label: string; amount: number }[] {
    const buckets = new Map<string, number>();
    const add = (code: string | undefined, rate: number | undefined, amount: number | undefined | null) => {
      if (amount === undefined || amount === null) return;
      const label = this.taxLabel(code, rate) || 'Tax';
      buckets.set(label, (buckets.get(label) ?? 0) + amount);
    };
    this.LineItems().forEach((i) => add(i.taxCode, i.taxRate, i.taxAmount));
    this.DiscountLineItems().forEach((d) => add(d.taxCode, d.taxRate, d.taxAmount === undefined || d.taxAmount === null ? null : -d.taxAmount));
    const delivery = this.BasketDelivery();
    if (delivery) add(delivery.taxCode, delivery.taxRate, delivery.taxAmount);
    this.TaxLineItems().forEach((t) => add(t.code, t.rate, t.amount));
    return Array.from(buckets, ([label, amount]) => ({ label, amount: Math.round(amount * 100) / 100 }));
  }

  public copyBasketJson(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    const basket = this.transaction().basket;
    if (!basket) return;
    navigator.clipboard?.writeText(JSON.stringify(basket, null, 2)).then(() => {
      this.notificationService.SmallDialog('Basket JSON copied to clipboard', 'Close', 2000);
    });
  }

  public billingAddressLines(): string[] {
    return addressLines(this.transaction().billingAddress);
  }

  public deliveryAddressLines(): string[] {
    return addressLines(this.transaction().deliveryAddress);
  }

  public taxTypeLabel(): string {
    const taxType = this.transaction().basket?.taxType;
    switch (typeof taxType === 'string' ? taxType.toLowerCase() : taxType) {
      case 1:
      case 'exclusive':
        return 'prices exclude tax';
      case 2:
      case 'exempt':
        return 'tax exempt';
      default:
        return 'prices include tax';
    }
  }

  // handlers

  public invoiceDetail: WritableSignal<InvoiceDetail | null> = signal<InvoiceDetail | null>(null);

  public printReceipt(): void {
    this.invoiceDetail.set(null);
    printPrintableContent();
  }

  public openInvoiceDialog(): void {
    const invoiceModal = this.dialog.open(InvoiceOptionsModalComponent, {
      data: new InvoiceModalData(this.transaction()),
      autoFocus: false,
      panelClass: ['main-panel', 'rounded-dialog'],
      width: '560px',
      maxWidth: '95vw',
    });
    invoiceModal.afterClosed().subscribe((options: InvoiceOptions | null) => {
      if (options == null) return;
      this.invoiceDetail.set(new InvoiceDetail(this.createReceipt(), options));
      printPrintableContent();
    });
  }

  public resendReceipt(): void {
    this.receiptService.resendPaymentConfirmation(this.transaction().crossReference).subscribe(() => {
      this.notificationService.SmallDialog('Receipt sent successfully');
    });
  }

  public startRefundRequest(): void {
    const refundRequestModal = this.dialog.open(
      RefundTransactionModalComponent,
      {
        data: new RefundModalData(this.transaction()),
        autoFocus: false,
        panelClass: ['main-panel', 'rounded-dialog'],
        maxWidth: 520,
      },
    );
    refundRequestModal.afterClosed().subscribe((refund: TransactionRefund) => {
      if (refund == null) return;
      this.refreshTransaction();
    });
  }

  public startRechargeRequest(): void {
    const rechargeRequestModal = this.dialog.open(
      RechargeTransactionModalComponent,
      {
        data: new RechargeModalData(this.transaction()),
        autoFocus: false,
        panelClass: ['main-panel', 'rounded-dialog'],
        maxWidth: 520,
      },
    );
    rechargeRequestModal
      .afterClosed()
      .subscribe((recharge: TransactionRecharge) => {
        if (recharge == null) return;
        this.refreshTransaction();
      });
  }

  public startPreAuthCompleteRequest(): void {
    const completeRequestModal = this.dialog.open(
      CompleteTransactionModalComponent,
      {
        data: new CompleteModalData(this.transaction()),
        autoFocus: false,
        panelClass: 'main-panel',
        maxWidth: 400,
      },
    );
    completeRequestModal.afterClosed().subscribe((completion: ITransaction) => {
      setTimeout(() => {
        this.refreshTransaction();
      }, 500);
    });
  }

  public startReverseRequest(): void {
    const reverseRequestModal = this.dialog.open(
      ReverseTransactionModalComponent,
      {
        data: new ReverseModalData(this.transaction()),
        autoFocus: false,
        panelClass: 'main-panel',
        maxWidth: 400,
      },
    );
    reverseRequestModal.afterClosed().subscribe(() => {
      this.refreshTransaction();
    });
  }
}
